import { motion } from "framer-motion";
import { ShieldCheck, Award, Users, Star, Quote } from "lucide-react";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "FCA Regulated Partners", sublabel: "We Only Work With The Best" },
  { icon: Award, label: "Trusted Service", sublabel: "Verified Introductions" },
  { icon: Users, label: "Thousands", sublabel: "People Connected" },
  { icon: Star, label: "5 Star", sublabel: "Customer Service" },
];

const TESTIMONIALS = [
  {
    name: "Sarah M.",
    location: "Manchester",
    quote: "SolveDebts connected me with a great debt advisor. I was drowning in £32,000 of debt and now I have an IVA that reduced my monthly payments by 60%.",
    image: "https://images.pexels.com/photos/7244703/pexels-photo-7244703.jpeg",
    debt_written: "£19,000",
    rating: 5
  },
  {
    name: "James T.",
    location: "Birmingham",
    quote: "The introduction process was quick and professional. The company they connected me with was incredibly supportive. My IVA was approved within weeks.",
    image: null,
    debt_written: "£24,500",
    rating: 5
  },
  {
    name: "Emma R.",
    location: "London",
    quote: "I wish I'd found SolveDebts sooner. They matched me with the right debt solution provider and now I have one affordable monthly payment.",
    image: null,
    debt_written: "£16,800",
    rating: 5
  }
];

const TrustSection = () => {
  return (
    <section className="pt-20 pb-12 md:pt-24 md:pb-16 bg-white" data-testid="trust-section">
      <div className="container-main">
        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {TRUST_BADGES.map((badge, index) => {
              const Icon = badge.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 text-center border border-slate-100 hover:shadow-lg transition-shadow"
                  data-testid={`trust-badge-${index}`}
                >
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-[#10B981]" />
                  </div>
                  <p className="font-bold text-xl text-[#0F172A]">{badge.label}</p>
                  <p className="text-sm text-slate-500">{badge.sublabel}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            Real People, Real Results
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Join thousands of UK residents who have taken control of their finances with our help
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="testimonial-card"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              <Quote className="w-8 h-8 text-emerald-200 mb-3" />
              
              <p className="text-slate-600 mb-6 leading-relaxed">
                "{testimonial.quote}"
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  {testimonial.image ? (
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-[#10B981] font-semibold text-lg">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-[#0F172A]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.location}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400 uppercase">Written Off</p>
                  <p className="font-numbers font-bold text-[#10B981]">{testimonial.debt_written}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16"
        >
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://images.pexels.com/photos/5550910/pexels-photo-5550910.jpeg"
              alt="Financial growth and freedom"
              className="w-full h-72 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/70 to-transparent flex items-start pt-12 md:pt-16">
              <div className="p-8 md:p-12 max-w-xl">
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-4">
                  Your Debt-Free Journey Starts Here
                </h3>
                <p className="text-slate-200 text-lg md:text-xl leading-relaxed">
                  Let us connect you with the right debt solution provider. Our free assessment takes just 2 minutes.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSection;
